import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Chat, ChatLabel, ChatMessage, MessageKind, TeamChat, TeamPerson, ThreadItem } from './types';
import { SAMPLE_REPLIES, sampleChats, sampleHeadOfficeChats, sampleTeamChats } from './sample';
import { useAuth } from '@/lib/auth';
import { LiveChatsProvider } from './live';
import { infoChangeLines } from '@/lib/teamInfo';

// The one data layer the screens use. Two insides, one shape: sample data that plays
// back ticks and replies (signed out, and local previews), and live chats from Sentinel
// for everyone signed in (live.tsx). The database's row rules decide what each person sees:
// their own chats, or every chat for head office (Gary, 18 Oct: the live list is gone).

export function clock(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

let seq = 0;
function nextId(): string {
  seq += 1;
  return `m${Date.now()}-${seq}`;
}

export function isMessage(item: ThreadItem): item is ChatMessage {
  return item.from !== 'day' && item.from !== 'lead';
}

/** One line of text for a message, used in quotes, the chat list and forwarding. */
export function previewOf(body: MessageKind): string {
  if (body.kind === 'text') return body.text;
  if (body.kind === 'photo') return '📷 Photo';
  if (body.kind === 'voice') return `🎤 Voice note (${body.duration})`;
  return body.title;
}

export interface OutgoingMessage {
  body: MessageKind;
  approved?: boolean;
  button?: string;
  note?: boolean;
  replyTo?: ChatMessage['replyTo'];
  forwarded?: boolean;
  /** Head office inboxes: sign as e.g. "Gary, CleanMyBed - Accounts" instead of the team alone. */
  signWithName?: boolean;
  /** "Busy, back soon": the server sends its fixed holding message and reminds the owner in an hour. */
  busy?: boolean;
  /** A page to open from a button under the words, instead of a raw link (Quick links, Gary 16 Oct). */
  link?: LinkButton;
}

/** A WhatsApp button that opens a page: the button's words (at most 20) and the page. */
export interface LinkButton { label: string; url: string }

export interface ChatsStore {
  /** True on real chats from Sentinel; false on sample data. */
  live: boolean;
  /** A message is on its way to WhatsApp: the send button waits. */
  sending: boolean;
  chats: Chat[];
  team: TeamChat[];
  typing: Record<string, boolean>;
  /** WhatsApp's "Keep chats archived": off means a new message brings the chat back. */
  keepArchived: boolean;
  setKeepArchived: (on: boolean) => void;
  markRead: (chatId: string) => void;
  setRead: (chatIds: string[], read: boolean) => void;
  setArchived: (chatIds: string[], archived: boolean) => void;
  setPinned: (chatIds: string[], pinned: boolean) => void;
  setMuted: (chatIds: string[], muted: boolean) => void;
  send: (chatId: string, message: OutgoingMessage) => void;
  /** A photo or PDF from the phone, with an optional caption. */
  sendFile: (chatId: string, file: File, caption?: string) => void;
  /** Resolves true once it went, so Forward says "Forwarded" only then (mobile review, 19 Oct). */
  sendTeam: (teamId: string, text: string, forwarded?: boolean, replyToId?: string, asLeo?: boolean, live?: boolean) => Promise<boolean>;
  /** Team chats, the same actions as a customer chat (Gary, 15 Oct). */
  sendTeamFile: (teamId: string, file: File, caption?: string, live?: boolean) => void;
  teamReact: (teamId: string, messageId: string, emoji: string | null) => void;
  teamStar: (teamId: string, messageId: string) => void;
  teamHide: (teamId: string, messageId: string) => void;
  /** Your own Team message within 15 minutes (Gary, 16 Oct): change its text, or delete it for everyone. */
  teamEdit: (teamId: string, messageId: string, text: string) => void;
  teamDelete: (teamId: string, messageId: string) => void;
  /** Forwards a photo, PDF or voice note as the file itself, from a customer or team chat. */
  forwardMedia: (sourceKind: 'chat' | 'team', messageId: string, targetKind: 'chat' | 'team', targetId: string) => Promise<boolean>;
  /** Team tab: everyone with access (All), opening a one-to-one chat, read and archive. */
  teamPeople: TeamPerson[];
  openTeam: (personId: string) => Promise<string | null>;
  markTeamRead: (teamId: string) => void;
  /** Platform Health: mark a Sentinel post done (it leaves the channel for everyone) or bring it back. */
  markTeamDone: (messageId: string, done: boolean) => void;
  setTeamArchived: (teamId: string, archived: boolean) => void;
  setTeamMuted: (teamId: string, muted: boolean) => void;
  /** Groups and channels (Gary, 15 Oct). Each resolves with an error message, or null when done. */
  createGroup: (name: string, memberIds: string[]) => Promise<{ id?: string; error?: string }>;
  addTeamMembers: (teamId: string, memberIds: string[]) => Promise<string | null>;
  removeTeamMember: (teamId: string, personId: string) => Promise<string | null>;
  setTeamAdmin: (teamId: string, personId: string, admin: boolean) => Promise<string | null>;
  /** A group's or channel's name and description (Gary, 17 Oct). True when saved; a refusal is shown as a toast. */
  editTeamInfo: (teamId: string, name: string, description: string) => Promise<boolean>;
  createChannel: (name: string) => Promise<{ id?: string; error?: string }>;
  /** Ted coaching a group (Gary, 1 Nov 2026): head office picks who he coaches and switches him on. Error or null. */
  setGroupCoach: (teamId: string, personIds: string[], on: boolean) => Promise<string | null>;
  react: (chatId: string, messageId: string, emoji: string | null) => void;
  toggleStar: (chatId: string, messageId: string) => void;
  deleteForMe: (chatId: string, messageId: string) => void;
  takeOver: (chatId: string, signOff: string) => void;
  /** Dr C on a live chat: hand it back to Dr C, or switch Dr C off for it. */
  drcAction: (chatId: string, action: 'return_to_ai' | 'ai_off') => void;
  /** Head office tells Leo something about this chat; he can answer again at once (Gary, 22 Oct 2026). */
  tellLeo: (chatId: string, text: string, replyNow: boolean) => void;
  /** Leo off for this chat's customer, or back on, in every chat with them (Gary, 20 Oct). */
  setLeoOff: (chatId: string, off: boolean) => void;
  reassign: (chatId: string, to: { id: string; name: string }) => void;
  /** Done with the chat: booked, or closed. It leaves the list and Sentinel keeps the record. */
  closeChat: (chatId: string, outcome: 'booked' | 'closed') => void;
  /** Every active shared label, for the label picker. */
  labels: ChatLabel[];
  setLabel: (chatId: string, labelId: string, on: boolean) => void;
  /** Starts a chat with a customer. WhatsApp only allows an approved message first. */
  startChat: (customer: { name: string; area: string }, approvedText: string) => string;
}

const SAMPLE_LABELS: ChatLabel[] = [
  { id: 'sample-unpaid', name: 'Unpaid', colour: 'orange' },
  { id: 'sample-quote', name: 'Quote sent', colour: 'blue' },
  { id: 'sample-follow', name: 'Follow up', colour: 'violet' },
];

export const StoreContext = createContext<ChatsStore | null>(null);

export function readKeepArchived(): boolean {
  try {
    return localStorage.getItem('chats.keepArchived') === '1';
  } catch {
    return false;
  }
}

export function ChatsProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const preview = import.meta.env.DEV && sessionStorage.getItem('chats.preview') !== null;
  if (!preview && session) {
    return <LiveChatsProvider userId={session.user.id}>{children}</LiveChatsProvider>;
  }
  return <SampleChatsProvider>{children}</SampleChatsProvider>;
}

function SampleChatsProvider({ children }: { children: ReactNode }) {
  const [chats, setChats] = useState<Chat[]>(() => [...sampleHeadOfficeChats(), ...sampleChats()]);
  const [team, setTeam] = useState<TeamChat[]>(() => sampleTeamChats());
  const [typing, setTyping] = useState<Record<string, boolean>>({});
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const [keepArchived, setKeepArchivedState] = useState(readKeepArchived);
  const keepArchivedRef = useRef(keepArchived);
  const replies = useRef<Record<string, string[]>>(Object.fromEntries(Object.entries(SAMPLE_REPLIES).map(([k, v]) => [k, [...v]])));
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  const setKeepArchived = useCallback((on: boolean) => {
    keepArchivedRef.current = on;
    setKeepArchivedState(on);
    try {
      localStorage.setItem('chats.keepArchived', on ? '1' : '0');
    } catch {
      // Storage blocked: the setting still holds for this visit.
    }
  }, []);

  const later = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const update = useCallback((chatId: string, fn: (chat: Chat) => Chat) => {
    setChats((list) => list.map((c) => (c.id === chatId ? fn(c) : c)));
  }, []);

  const updateMany = useCallback((chatIds: string[], fn: (chat: Chat) => Chat) => {
    const ids = new Set(chatIds);
    setChats((list) => list.map((c) => (ids.has(c.id) ? fn(c) : c)));
  }, []);

  const updateMessage = useCallback((chatId: string, messageId: string, fn: (m: ChatMessage) => ChatMessage) => {
    update(chatId, (c) => ({ ...c, thread: c.thread.map((m) => (m.id === messageId && isMessage(m) ? fn(m) : m)) }));
  }, [update]);

  /** A customer message arrives: count it, and bring the chat back from the archive unless kept archived. */
  const receive = useCallback((chatId: string, items: ThreadItem[], patch: Partial<Chat> = {}) => {
    update(chatId, (c) => ({
      ...c,
      ...patch,
      unread: c.unread + 1,
      time: clock(),
      archived: c.archived && keepArchivedRef.current,
      thread: [...c.thread, ...items],
    }));
  }, [update]);

  const markRead = useCallback((chatId: string) => update(chatId, (c) => (c.unread || c.markedUnread ? { ...c, unread: 0, markedUnread: false } : c)), [update]);

  const setRead = useCallback((chatIds: string[], read: boolean) => {
    updateMany(chatIds, (c) => (read ? { ...c, unread: 0, markedUnread: false } : { ...c, markedUnread: c.unread === 0 }));
  }, [updateMany]);

  const setArchived = useCallback((chatIds: string[], archived: boolean) => {
    updateMany(chatIds, (c) => ({ ...c, archived, pinned: archived ? false : c.pinned }));
  }, [updateMany]);

  const setPinned = useCallback((chatIds: string[], pinned: boolean) => updateMany(chatIds, (c) => ({ ...c, pinned })), [updateMany]);
  const setMuted = useCallback((chatIds: string[], muted: boolean) => updateMany(chatIds, (c) => ({ ...c, muted })), [updateMany]);

  const send = useCallback((chatId: string, message: OutgoingMessage) => {
    const id = nextId();
    const at = clock();
    update(chatId, (c) => {
      const out: ChatMessage = message.note
        ? { id, from: 'note', body: message.body, at, replyTo: message.replyTo }
        : {
            id, from: 'out', body: message.body, at, delivery: 'pending',
            approved: message.approved, button: message.button, replyTo: message.replyTo, forwarded: message.forwarded,
            signOff: !message.approved && c.brand === 'cmb' ? c.signOff : undefined,
          };
      return {
        ...c,
        thread: [...c.thread, out],
        time: at,
        archived: false,
        answered: c.dueMinutes !== undefined ? true : c.answered,
        state: c.state === 'new' ? 'mine' : message.approved ? 'waiting' : c.state,
      };
    });
    if (message.note) return;

    later(500, () => updateMessage(chatId, id, (m) => ({ ...m, delivery: 'sent' })));
    later(1300, () => updateMessage(chatId, id, (m) => ({ ...m, delivery: 'delivered', deliveredAt: clock() })));
    later(2600, () => updateMessage(chatId, id, (m) => ({ ...m, delivery: 'read', readAt: clock() })));

    // An approved message to a closed chat: the customer answers and the chat opens again.
    if (message.approved) {
      later(4500, () => {
        const name = chatsRef.current.find((x) => x.id === chatId)?.name ?? '';
        const first = name.split(' ')[0] || 'The customer';
        receive(chatId, [
          { id: nextId(), from: 'in', body: { kind: 'text', text: 'Yes please, Friday if you can' }, at: clock() },
          { id: nextId(), from: 'system', body: { kind: 'text', text: `${first} replied. You can chat normally for the next 24 hours.` }, at: clock() },
        ], { windowOpen: true, state: 'mine', quickReplies: ['Offer Friday slots', 'Send price list'] });
      });
      return;
    }

    const reply = replies.current[chatId]?.shift();
    if (!reply) return;
    later(3200, () => setTyping((t) => ({ ...t, [chatId]: true })));
    later(5600, () => {
      setTyping((t) => ({ ...t, [chatId]: false }));
      receive(chatId, [{ id: nextId(), from: 'in', body: { kind: 'text', text: reply }, at: clock() }]);
      // The customer reacts to what we sent, as people do.
      later(1500, () => updateMessage(chatId, id, (m) => ({ ...m, theirReaction: '👍' })));
    });
  }, [later, receive, update, updateMessage]);

  // Sample chats: the file shows in the thread straight from the phone, nothing is sent.
  const sendFile = useCallback((chatId: string, file: File, caption?: string) => {
    const url = URL.createObjectURL(file);
    send(chatId, {
      body: file.type.startsWith('image/')
        ? { kind: 'photo', caption: caption ?? '', url }
        : { kind: 'link', title: file.name || 'Document', url, text: caption ?? '', file: file.type === 'application/pdf' ? 'PDF' : 'Document' },
    });
  }, [send]);

  const sendTeam = useCallback((teamId: string, text: string, forwarded = false) => {
    const at = clock();
    setTeam((list) => list.map((t) => (t.id === teamId
      ? { ...t, time: at, thread: [...t.thread, { id: nextId(), from: 'out', body: { kind: 'text', text }, at, delivery: 'read', forwarded }] }
      : t)));
    return Promise.resolve(true);
  }, []);

  const react = useCallback((chatId: string, messageId: string, emoji: string | null) => {
    updateMessage(chatId, messageId, (m) => ({ ...m, reaction: emoji ?? undefined }));
  }, [updateMessage]);

  const toggleStar = useCallback((chatId: string, messageId: string) => {
    updateMessage(chatId, messageId, (m) => ({ ...m, starred: !m.starred }));
  }, [updateMessage]);

  const deleteForMe = useCallback((chatId: string, messageId: string) => {
    update(chatId, (c) => ({ ...c, thread: c.thread.filter((m) => m.id !== messageId) }));
  }, [update]);

  const takeOver = useCallback((chatId: string, signOff: string) => {
    update(chatId, (c) => ({
      ...c,
      takenOver: true,
      owner: 'Head office',
      dueMinutes: undefined,
      signOff,
      thread: [...c.thread, { id: nextId(), from: 'system', body: { kind: 'text', text: 'Head office took over this chat. The operator can still read it.' }, at: clock() }],
    }));
  }, [update]);

  const reassign = useCallback((chatId: string, to: { id: string; name: string }) => {
    update(chatId, (c) => ({
      ...c,
      owner: to.name,
      thread: [...c.thread, { id: nextId(), from: 'system', body: { kind: 'text', text: `Reassigned to ${to.name}. They have been notified.` }, at: clock() }],
    }));
  }, [update]);

  const closeChat = useCallback((chatId: string, outcome: 'booked' | 'closed') => {
    update(chatId, (c) => ({
      ...c,
      state: outcome === 'booked' ? 'booked' : c.state,
      archived: outcome === 'closed' ? true : c.archived,
      closed: outcome === 'closed' ? true : c.closed,
      pinned: outcome === 'closed' ? false : c.pinned,
      dueMinutes: undefined,
      thread: [...c.thread, { id: nextId(), from: 'system', body: { kind: 'text', text: outcome === 'booked' ? 'Marked booked' : 'Chat closed' }, at: clock() }],
    }));
  }, [update]);

  const startChat = useCallback((customer: { name: string; area: string }, approvedText: string) => {
    const id = `new-${nextId()}`;
    const at = clock();
    const messageId = nextId();
    setChats((list) => [{
      id, brand: 'cmb', name: customer.name, area: customer.area, colour: list.length % 6,
      state: 'waiting', unread: 0, time: at, windowOpen: false, owner: 'You', quickReplies: [],
      thread: [
        { id: nextId(), from: 'day', label: 'Today' },
        { id: messageId, from: 'out', approved: true, body: { kind: 'text', text: approvedText }, at, delivery: 'pending' },
      ],
    }, ...list]);
    later(600, () => updateMessage(id, messageId, (m) => ({ ...m, delivery: 'delivered', deliveredAt: clock() })));
    return id;
  }, [later, updateMessage]);

  return (
    <StoreContext.Provider
      value={{
        live: false, sending: false,
        chats, team, typing, keepArchived, setKeepArchived,
        markRead, setRead, setArchived, setPinned, setMuted,
        send, sendFile, sendTeam, react, toggleStar, deleteForMe, takeOver, reassign, closeChat, startChat,
        // Sample data: the switch flips on screen (Gary, 23 Oct demo).
        drcAction: (chatId, action) => update(chatId, (c) => ({ ...c, aiState: action === 'ai_off' ? 'human' as const : 'ai' as const, onDuty: action === 'ai_off' ? 'owner' as const : 'leo' as const })),
        tellLeo: () => undefined,
        setLeoOff: (chatId, off) => update(chatId, (c) => ({ ...c, leoOff: off, ...(off && c.aiState === 'ai' ? { aiState: 'human' as const } : {}) })),
        labels: SAMPLE_LABELS,
        teamPeople: team.map((t) => ({ id: t.id, name: t.name })),
        sendTeamFile: () => undefined,
        forwardMedia: () => Promise.resolve(true),
        teamReact: (teamId, messageId, emoji) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, thread: t.thread.map((m) => (m.id === messageId && m.from !== 'day' && m.from !== 'lead' ? { ...m, reaction: emoji ?? undefined } : m)) } : t))),
        teamStar: (teamId, messageId) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, thread: t.thread.map((m) => (m.id === messageId && m.from !== 'day' && m.from !== 'lead' ? { ...m, starred: !m.starred } : m)) } : t))),
        teamHide: (teamId, messageId) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, thread: t.thread.filter((m) => m.id !== messageId) } : t))),
        teamEdit: (teamId, messageId, text) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, thread: t.thread.map((m) => (m.id === messageId && m.from !== 'day' && m.from !== 'lead' ? { ...m, body: { kind: 'text' as const, text }, edited: true } : m)) } : t))),
        teamDelete: (teamId, messageId) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, thread: t.thread.map((m) => (m.id === messageId && m.from !== 'day' && m.from !== 'lead' ? { ...m, body: { kind: 'text' as const, text: 'This message was deleted' }, deleted: true } : m)) } : t))),
        openTeam: (personId) => Promise.resolve(personId),
        markTeamRead: () => undefined,
        markTeamDone: (messageId, done) => setTeam((list) => list.map((t) => ({ ...t, thread: t.thread.map((m) => (m.id === messageId && m.from !== 'day' && m.from !== 'lead' ? { ...m, done: done ? { by: 'you', at: 'now' } : undefined } : m)) }))),
        createGroup: () => Promise.resolve({ error: 'Groups work on live chats only' }),
        addTeamMembers: () => Promise.resolve(null),
        removeTeamMember: () => Promise.resolve(null),
        setTeamAdmin: () => Promise.resolve(null),
        setGroupCoach: () => Promise.resolve('Ted works on live chats only'),
        editTeamInfo: (teamId, name, description) => {
          setTeam((list) => list.map((t) => {
            if (t.id !== teamId || (t.kind !== 'group' && t.kind !== 'channel')) return t;
            const lines = infoChangeLines(t.kind, t, name, description);
            return {
              ...t, name, description: description || undefined,
              thread: [...t.thread, ...lines.map((line): ThreadItem => ({ id: nextId(), from: 'system', body: { kind: 'text', text: `You ${line}` }, at: clock() }))],
            };
          }));
          return Promise.resolve(true);
        },
        createChannel: () => Promise.resolve({ error: 'Channels work on live chats only' }),
        setTeamMuted: (teamId, muted) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, muted } : t))),
        setTeamArchived: (teamId, archived) => setTeam((list) => list.map((t) => (t.id === teamId ? { ...t, archived } : t))),
        setLabel: (chatId, labelId, on) => update(chatId, (c) => ({
          ...c,
          labels: on
            ? [...(c.labels ?? []), ...SAMPLE_LABELS.filter((l) => l.id === labelId)]
            : (c.labels ?? []).filter((l) => l.id !== labelId),
        })),
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useChats(): ChatsStore {
  const value = useContext(StoreContext);
  if (!value) throw new Error('useChats must be used inside ChatsProvider');
  return value;
}
